import { Injectable, OnDestroy, effect, signal } from '@angular/core';

type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly THEME_STORAGE_KEY = 'agents-booster-theme';
  /** Tracks whether the user explicitly chose a theme via toggle (vs. OS-derived). */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly EXPLICIT_PREF_KEY = 'agents-booster-theme-explicit';

  private readonly themeSignal = signal<Theme>(this.getInitialTheme());
  /** True when the active theme was set by the user, false when following OS. */
  private hasExplicitPreference = !!localStorage.getItem(this.EXPLICIT_PREF_KEY);

  constructor() {
    // Sync theme changes to the DOM and storage
    effect(() => {
      const theme = this.themeSignal();
      this.applyThemeToDom(theme);
      if (this.hasExplicitPreference) {
        this.saveThemeToStorage(theme);
      }
    });

    // Follow OS theme changes when the user has not set an explicit preference
    this.setupOsThemeListener();
  }

  /**
   * El `data-theme` de `<html>` lo pone este servicio y se va con él. En la app no se destruye
   * nunca; en los tests cada inyector raíz que se destruía dejaba el tema oscuro puesto para el
   * archivo siguiente (refutación rt2).
   */
  public ngOnDestroy(): void {
    delete document.documentElement.dataset['theme'];
  }

  /**
   * Returns the initial theme based on:
   * 1. Explicit user preference saved in localStorage
   * 2. Current OS preference (prefers-color-scheme)
   * 3. Falls back to 'light'
   */
  private getInitialTheme(): Theme {
    // 1. Explicit user preference
    const stored = localStorage.getItem(this.THEME_STORAGE_KEY) as Theme | null;
    const hasExplicit = !!localStorage.getItem(this.EXPLICIT_PREF_KEY);
    if (hasExplicit && stored && (stored === 'light' || stored === 'dark')) {
      return stored;
    }

    // 2. OS preference
    return this.getOsTheme();
  }

  /** Reads the current OS color-scheme preference. */
  private getOsTheme(): Theme {
    if (!('matchMedia' in globalThis)) return 'light';
    if (globalThis.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  }

  /**
   * Listens for OS color-scheme changes and updates the theme signal
   * when no explicit user preference is active.
   */
  private setupOsThemeListener(): void {
    if (!('matchMedia' in globalThis)) return;
    globalThis
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (event: MediaQueryListEvent) => {
        if (!this.hasExplicitPreference) {
          this.themeSignal.set(event.matches ? 'dark' : 'light');
        }
      });
  }

  /**
   * Applies the theme to the <html> element via data-theme attribute.
   * Tailwind uses html[data-theme="dark"] as the dark-mode selector.
   */
  private applyThemeToDom(theme: Theme): void {
    if (theme === 'dark') {
      document.documentElement.dataset['theme'] = 'dark';
    } else {
      delete document.documentElement.dataset['theme'];
    }
  }

  private saveThemeToStorage(theme: Theme): void {
    localStorage.setItem(this.THEME_STORAGE_KEY, theme);
  }

  public getTheme() {
    return this.themeSignal.asReadonly();
  }

  public getCurrentTheme(): Theme {
    return this.themeSignal();
  }

  public isDarkMode(): boolean {
    return this.themeSignal() === 'dark';
  }

  /**
   * Sets the theme explicitly (user action).
   * Marks this as an explicit preference so OS changes won't override it.
   */
  public setTheme(theme: Theme): void {
    this.hasExplicitPreference = true;
    localStorage.setItem(this.EXPLICIT_PREF_KEY, 'true');
    this.saveThemeToStorage(theme);
    this.themeSignal.set(theme);
  }

  /**
   * Removes the explicit user preference and reverts to following the OS theme.
   */
  public resetToOsTheme(): void {
    this.hasExplicitPreference = false;
    localStorage.removeItem(this.EXPLICIT_PREF_KEY);
    localStorage.removeItem(this.THEME_STORAGE_KEY);
    this.themeSignal.set(this.getOsTheme());
  }

  public toggleTheme(): void {
    this.setTheme(this.themeSignal() === 'light' ? 'dark' : 'light');
  }
}
